import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';

import { firstValueFrom } from 'rxjs';

import { ConfirmationService } from '../../../core/confirm/confirmation-dialog.service';
import { SnackBarService } from '../../../core/services/snack-bar.service';
import { ScenariosDao } from '../../../data/daos/scenario.dao';
import { WithDbMeta } from '../../../data/models/base.model';
import { Scenario } from '../../../data/models/scenario.model';
import { fmtDialogConfig } from '../../../portal/financial-modeling/fmt-dialog.util';
import { fmtRoute, getFmtPageFromUrl, isFmtRoute } from '../../../portal/financial-modeling/fmt-route.util';
import { ActiveScenarioService } from '../../../portal/financial-modeling/services/active-scenario.service';
import {
	NewScenarioDialogComponent, NewScenarioDialogData, NewScenarioDialogResult,
} from '../../../portal/financial-modeling/shared/new-scenario-dialog.component';
import { AppRoute, FmtPage } from '../../constants/route';
import { LocalStorageUtil } from '../../utils/local-storage.util';

@Component({
	selector: 'dix-scenario-nav',
	templateUrl: './scenario-nav.component.html',
	styleUrl: './scenario-nav.component.scss',
	imports: [FormsModule, MatButtonModule, MatIconModule],
})
export class ScenarioNavComponent {
	readonly activeScenarioService = inject(ActiveScenarioService);
	private readonly scenariosRaw = toSignal(this.activeScenarioService.streamScenarios(), {
		initialValue: [] as WithDbMeta<Scenario>[],
	});
	readonly scenarios = computed(() => sortByName(this.scenariosRaw()));
	readonly renamingId = signal<string | null>(null);
	readonly renameValue = signal('');

	private readonly router = inject(Router);
	private readonly dialog = inject(MatDialog);
	private readonly snackBar = inject(SnackBarService);
	private readonly confirmation = inject(ConfirmationService);
	private readonly scenariosDao = inject(ScenariosDao);

	async switchScenario(scenarioId: string): Promise<void> {
		const onFmt = isFmtRoute(this.router.url);
		if (scenarioId === this.activeScenarioService.scenarioId()) {
			if (!onFmt) {
				await this.router.navigate(fmtRoute(scenarioId, FmtPage.Overview));
			}
			return;
		}
		if (onFmt) {
			const page = getFmtPageFromUrl(this.router.url) ?? FmtPage.Overview;
			await this.router.navigate(fmtRoute(scenarioId, page));
		} else {
			await this.router.navigate(fmtRoute(scenarioId, FmtPage.Overview));
		}
	}

	startRename(scenarioId: string, name: string, event: Event): void {
		event.stopPropagation();
		this.renamingId.set(scenarioId);
		this.renameValue.set(name);
	}

	async commitRename(scenarioId: string): Promise<void> {
		const name = this.renameValue().trim();
		this.renamingId.set(null);
		if (name === '') return;
		const current = this.scenarios().find((s) => s.id === scenarioId);
		if (current == null || current.name === name) return;
		await this.scenariosDao.doc({ clientId: LocalStorageUtil.getActiveClientId() }, scenarioId).update({ name });
	}

	deleteScenario(scenarioId: string, event: Event): void {
		event.stopPropagation();
		const scenario = this.scenarios().find((s) => s.id === scenarioId);
		if (scenario == null) return;
		this.confirmation.confirm({
			title: 'Delete scenario',
			question: `Are you sure you want to delete "${scenario.name}"?`,
			details: 'This cannot be undone.',
			confirm: 'Delete',
			isConfirmationNegative: true,
			failureMessage: 'Failed to delete scenario.',
			onConfirm: async () => {
				const wasActive = scenarioId === this.activeScenarioService.scenarioId();
				const onFmt = isFmtRoute(this.router.url);
				const remaining = this.scenarios().filter((s) => s.id !== scenarioId);
				await this.activeScenarioService.deleteScenario(scenarioId);
				if (wasActive && onFmt) {
					const nextId = remaining[0]?.id;
					if (nextId != null) {
						await this.router.navigate(fmtRoute(nextId, FmtPage.Overview));
					} else {
						await this.router.navigate([AppRoute.FinancialModeling, '_', FmtPage.Overview]);
					}
				}
				this.snackBar.open('Scenario deleted.');
			},
		});
	}

	async openNewScenarioDialog(): Promise<void> {
		const dialogRef = this.dialog.open<NewScenarioDialogComponent, NewScenarioDialogData, NewScenarioDialogResult>(
			NewScenarioDialogComponent,
			{
				...fmtDialogConfig('420px'),
				autoFocus: true,
				data: { scenarios: this.scenarios() },
			},
		);
		const result = await firstValueFrom(dialogRef.afterClosed());
		if (result == null) return;
		try {
			const id = await this.activeScenarioService.createScenario(result.name, result.copyFromId);
			await this.router.navigate(fmtRoute(id, FmtPage.Overview));
			this.snackBar.open('Scenario created.');
		} catch {
			this.snackBar.open('Failed to create scenario.');
		}
	}
}

function sortByName<T extends { name?: string }>(items: T[]): T[] {
	return [...items].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }));
}
